import {
  Alert,
  AlertIcon,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import InputSelectMobile from '../../../../../components/Input/Mobile/Select';
import { useRequestSelectiveProcessVacancy } from '../../../../../services/hooks/SelectiveProcess/useRequestSelectiveProcessVacancy';
import { PiIdentificationBadgeFill } from 'react-icons/pi';
import { useFormContext } from 'react-hook-form';
import { ETypeOfCompetition } from '../../../../../components/pages/Registration/DefaultVacancy';
import { UseRegistration } from '../../../../../Store/useRegistration';
import TitleSectionMobilePage from '../../../../../components/Mobile/titleSection';
import { IDataWheelPickerSelect } from '../../../../../components/Input/Mobile/Select/whellPicker';
import InputMobile from '../../../../../components/Input/Mobile';
import { GoNumber } from 'react-icons/go';
import InputAttachmentFile from '../../../Resources/mobile/create/attachmentFile';
import PcdAttachments from '../../../../../components/pages/Registration/PcdAttachments';
import { useGetRegistration } from '../../../../../services/hooks/Registration/useGetRegistration';
import { handleDeleteAttach } from '../../../../../utils/functions';
import { IResponseIdName, useRequestQuery } from '../../../../../services/hooks/globalQuery';
import React, { ChangeEvent, useMemo } from 'react';
import { IDataToRegistration } from '../../../../../services/hooks/CandidateArea/useGetDataToRegistration';
import {
  useRequestResearchLines,
  useRequestThematicAxes,
} from '../../../../../services/hooks/SelectiveProcess/useRequestRegistrationHierarchy';

interface IOwnProps {
  selectiveProcess?: IDataToRegistration;
}

const FormVacancyMobilePage: React.FC<IOwnProps> = ({ selectiveProcess }) => {
  const { register, watch, setValue, formState } = useFormContext();
  const { selectiveProcessId, registrationId } = UseRegistration();

  const { data: registration, refetch: refetchRegistration } = useGetRegistration(registrationId ?? '', Boolean(registrationId));

  const vacancy = useRequestSelectiveProcessVacancy(selectiveProcessId, selectiveProcess?.is_cultural_house ?? false);
  const subTypeCompetitions = useRequestQuery<IResponseIdName[]>({
    url: 'registration/sub-type-of-competition',
    key: ['list_sub_type_competition'],
    remove_pagination: true,
  });

  const toast = useToast();

  const accent = '#222';
  const selectedLevelId = watch('allow_filter_vacancy_tree');
  const selectedResearchLineId = watch('research_line');
  const selectedThematicAxisId = watch('thematic_axis');
  const researchLines = useRequestResearchLines(selectiveProcessId, selectedLevelId);
  const thematicAxes = useRequestThematicAxes(selectedResearchLineId);
  const hasResearchLineCascade = !selectiveProcess?.is_cultural_house && Boolean(researchLines.data?.length);
  const filteredVacancies = useRequestSelectiveProcessVacancy(
    selectiveProcessId,
    selectiveProcess?.is_cultural_house ?? false,
    '',
    '',
    {
      level_id: selectedLevelId,
      research_line_id: selectedResearchLineId,
      thematic_axis_id: selectedThematicAxisId,
    },
  );

  const onDeleteAttachment = async (id: string) => {
    await handleDeleteAttach(id, toast, () => {
      void refetchRegistration();
    });
  };

  const handleSetModality = (item?: IDataWheelPickerSelect) => {
    if (item) {
      setValue('type_of_competition', '');
      setValue('type_of_competition_id', '');
      setValue('modality_type', undefined);
    }
  };

  const pcdvacancy = registration?.reserved_vacancy?.find((rv) => rv.type?.toLowerCase()?.includes('pcd'));

  const subTypeOfCompetitionList = subTypeCompetitions.data
    ?.filter((subTypeCompetition: IResponseIdName) => {
      const normalizedName = subTypeCompetition.name.toLowerCase();
      const isBlackAndBrownCombined = normalizedName.includes('pret') && normalizedName.includes('pard');

      return isBlackAndBrownCombined || (!normalizedName.includes('pret') && !normalizedName.includes('pard'));
    })
    .map((subTypeCompetition: IResponseIdName) => {
      const selectedModality: (string | undefined)[] = watch('sub_type_of_competition') || [];
      const isChecked = selectedModality.includes(subTypeCompetition.id);

      if (selectiveProcess?.allow_filter_vacancy_tree) {
        return (
          <Radio key={subTypeCompetition.id} value={subTypeCompetition.id} size="md" fontSize="sm" mt={2}>
            {subTypeCompetition.name}
          </Radio>
        );
      }

      return (
        <React.Fragment key={subTypeCompetition.id}>
          <Checkbox
            isChecked={isChecked}
            onChange={(e) => {
              const value = subTypeCompetition.id;
              const current = watch('sub_type_of_competition') ?? [];

              const newValues = e.target.checked ? [...current, value] : current.filter((v: string) => v !== value);

              setValue('sub_type_of_competition', newValues, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            size="md"
            fontSize="sm"
            isRequired={false}
            lineHeight={1.5}
            mt={2}
          >
            {subTypeCompetition.name}
          </Checkbox>
        </React.Fragment>
      );
    });

  const selectVacancy = useMemo(() => {
    const vacancyId = watch('vacancy');
    if (vacancy?.data) {
      return vacancy?.data?.find((vac) => vacancyId === vac?.id);
    }

    return undefined;
  }, [vacancy?.data, watch('vacancy')]);

  const listModality = [
    ...(!selectiveProcess?.allow_filter_vacancy_tree ||
    (selectVacancy?.number_of_extensive_competition_vacancies ?? 0) > 0
      ? [{ id: ETypeOfCompetition.FULL_VACANCY, name: 'Ampla concorrência' }]
      : []),
    ...(!selectiveProcess?.allow_filter_vacancy_tree || (selectVacancy?.number_of_paa_vacancies ?? 0) > 0
      ? [
          {
            id: ETypeOfCompetition.SPECIAL_VACANCY,
            name: selectiveProcess?.allow_filter_vacancy_tree ? 'PAA' : 'Vagas reservadas',
          },
        ]
      : []),
    ...(selectiveProcess?.allow_filter_vacancy_tree && (selectVacancy?.number_of_ufc_server_vacancies ?? 0) > 0
      ? [{ id: ETypeOfCompetition.UFC_SERVER_VACANCY, name: 'S-UFC' }]
      : []),
  ];

  const modalityListFilter = selectVacancy?.type_of_competition
    ?.filter((modality: IResponseIdName) => {
      const normalizedName = modality.name.toLowerCase().replace(/[-_]/g, ' ');
      if (normalizedName.includes('ampla')) return false;
      if (normalizedName.includes('ufc')) return false;
      return true;
    })
    .map((modality: IResponseIdName) => {
      const selectedModality: (string | undefined)[] = watch('modality_type') || [];
      const isChecked = selectedModality.includes(modality.id);

      const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = modality.id;
        const current = watch('modality_type') ?? [];

        const newValues = e.target.checked
          ? !selectiveProcess?.allow_filter_vacancy_tree
            ? [...current, value]
            : [value]
          : current.filter((v: string) => v !== value);

        if (e.target.checked && selectiveProcess?.allow_filter_vacancy_tree) {
          if (modality.name.toLowerCase().includes('pcd')) {
            setValue('sub_type_of_competition', []);
          } else if (modality.name.toLowerCase().includes('pppiq')) {
            setValue('medical_report', undefined);
            setValue('cid', '');
            setValue('deficiency', '');
          }
        }

        setValue('modality_type', newValues, {
          shouldDirty: true,
          shouldValidate: true,
        });

        if (modality.name.toLowerCase().includes('pppiq')) {
          setValue('sub_type_of_competition', []);
        }
      };

      return (
        <React.Fragment key={modality.id}>
          <Switch
            isChecked={isChecked}
            onChange={handleChange}
            size="md"
            fontSize="sm"
            isRequired={false}
            colorScheme="green"
            lineHeight={1.5}
            mt={2}
          >
            {modality.name}
          </Switch>
          {(modality.name.toLowerCase().includes('pcd') && isChecked && (
            <Stack mt={3} gap={5}>
              <InputMobile
                name="cid"
                placeholderLabel="CID"
                iconLeft={<GoNumber size={12} color={accent} />}
                isRequired
              />
              <VStack gap={2} align="stretch" minW={0}>
                <TitleSectionMobilePage border="none" mb={0} fontSize="13px" fontWeight="normal" isRequired>
                  Envio de laudo - Pessoas com deficiencia (PCD):
                </TitleSectionMobilePage>
                <InputAttachmentFile
                  inputName="medical_report"
                />
                <PcdAttachments attachments={pcdvacancy?.attachments} onRemove={onDeleteAttachment} />
              </VStack>
              <FormControl
                isInvalid={!!formState.errors.deficiency?.message}
                sx={{
                  transition: 'padding-top 0.2s ease',
                  '&:has(input:focus), &:has(input:not(:placeholder-shown))': { paddingTop: '0.8rem' },
                }}
                isRequired={true}
                position="relative"
                h="initial"
              >
                <FormLabel fontWeight="normal" mb={1} fontSize="13px" transition="all 0.2s ease">
                  Descreva em detalhes a sua solicitação:
                </FormLabel>
                <Textarea
                  rounded="md"
                  px={2}
                  isRequired={false}
                  resize="vertical"
                  minH="120px"
                  {...register('deficiency')}
                />

                <FormErrorMessage fontSize="0.78rem">
                  {formState.errors.deficiency?.message?.toString()}
                </FormErrorMessage>
              </FormControl>
            </Stack>
          )) ||
            (modality.name.toLowerCase().includes('pppiq') && isChecked && (
              <Stack mt={3}>
                <FormControl
                  display="flex"
                  flexDir="column"
                  isInvalid={!!formState.errors.sub_type_of_competition?.message}
                >
                  <Stack gap={2}>
                    {selectiveProcess?.allow_filter_vacancy_tree ? (
                      <RadioGroup
                        value={(watch('sub_type_of_competition') ?? [])[0] ?? ''}
                        onChange={(value) => {
                          setValue('sub_type_of_competition', [value], {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                      >
                        <Stack>{subTypeOfCompetitionList}</Stack>
                      </RadioGroup>
                    ) : (
                      subTypeOfCompetitionList
                    )}
                    <FormErrorMessage fontSize="0.78rem">
                      {formState.errors.sub_type_of_competition?.message?.toString()}
                    </FormErrorMessage>
                    <Alert mt={4} status="info">
                      <AlertIcon />
                      <Text fontSize="sm" fontWeight="normal">
                        {selectiveProcess?.allow_filter_vacancy_tree
                          ? 'Selecione acima o grupo ao qual você se autodeclara pertencente (escolha apenas uma opção):'
                          : 'Selecione acima um ou mais grupos aos quais você se autodeclara pertencente:'}
                      </Text>
                    </Alert>
                  </Stack>
                </FormControl>
              </Stack>
            ))}
        </React.Fragment>
      );
    });

  const vacancyLevel = useMemo(() => {
    return Array.from(
      new Map(
        (vacancy.data ?? []).filter((item) => item.level?.id).map((item) => [item.level.id, item.level] as const),
      ).values(),
    );
  }, [vacancy.data]);

  const listVacancy = useMemo(() => {
    if (selectiveProcess?.allow_filter_vacancy_tree) {
      return vacancy.data?.filter((item) => item.level?.id === watch('allow_filter_vacancy_tree'));
    }

    return vacancy.data;
  }, [vacancyLevel, vacancy.data, selectiveProcess?.allow_filter_vacancy_tree, watch('allow_filter_vacancy_tree')]);
  const vacancyOptions = (hasResearchLineCascade ? filteredVacancies.data : listVacancy) ?? [];
  const showNoVacanciesMessage =
    Boolean(selectiveProcess?.allow_filter_vacancy_tree && selectedThematicAxisId) &&
    !filteredVacancies.isLoading &&
    vacancyOptions.length === 0;

  return (
    <Stack w="full" gap={3} mt={1}>
      {selectiveProcess?.allow_filter_vacancy_tree && (
        <InputSelectMobile
          name="allow_filter_vacancy_tree"
          placeholderLabel="Curso"
          iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
          isRequired
          isDisabled={vacancy.isLoading || vacancyLevel.length === 0}
          listItens={vacancyLevel}
          onChangeItem={() => {
            setValue('research_line', '');
            setValue('thematic_axis', '');
            setValue('vacancy', '');
          }}
        />
      )}
      {selectiveProcess?.allow_filter_vacancy_tree && (
        <>
          <InputSelectMobile
            name="research_line"
            placeholderLabel="Linha de pesquisa"
            iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
            isRequired
            isDisabled={!selectedLevelId || researchLines.isLoading || !researchLines.data?.length}
            listItens={researchLines.data ?? []}
            onChangeItem={() => {
              setValue('thematic_axis', '');
              setValue('vacancy', '');
            }}
          />
          <InputSelectMobile
            name="thematic_axis"
            placeholderLabel="Eixo temático"
            iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
            isRequired
            isDisabled={!selectedResearchLineId || thematicAxes.isLoading || !thematicAxes.data?.length}
            listItens={thematicAxes.data ?? []}
            onChangeItem={() => {
              setValue('vacancy', '');
            }}
          />
        </>
      )}
      <InputSelectMobile
        name="vacancy"
        placeholderLabel="Selecione a sua vaga/cargo"
        iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
        isRequired
        isDisabled={
          Boolean(selectiveProcess?.allow_filter_vacancy_tree && !selectedLevelId) ||
          researchLines.isLoading ||
          vacancyOptions.length === 0 ||
          (hasResearchLineCascade && (!selectedThematicAxisId || filteredVacancies.isLoading))
        }
        onChangeItem={handleSetModality}
        listItens={vacancyOptions}
      />
      {showNoVacanciesMessage && (
        <Alert status="warning" borderRadius="md" py={2}>
          <AlertIcon />
          <Text fontSize="sm">Não há vagas disponíveis para essa seleção</Text>
        </Alert>
      )}
      <InputSelectMobile
        name="type_of_competition"
        placeholderLabel="Selecione o tipo de competição"
        isDisabled={!watch('vacancy') || listModality.length === 0}
        iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
        isRequired
        listItens={listModality}
        onChangeItem={(item) => {
          const selectedTypeId = selectVacancy?.type_of_competition.find((type) => {
            const normalizedName = type.name.toLowerCase().replace(/[-_]/g, ' ');
            if (item?.id === ETypeOfCompetition.FULL_VACANCY) return normalizedName.includes('ampla');
            if (item?.id === ETypeOfCompetition.UFC_SERVER_VACANCY) return normalizedName.includes('ufc');
            return false;
          })?.id;

          setValue('type_of_competition_id', selectedTypeId ?? '', {
            shouldDirty: true,
          });
        }}
      />
      {watch('type_of_competition') == ETypeOfCompetition.SPECIAL_VACANCY && (
        <Stack mt={3}>
          <TitleSectionMobilePage fontSize="sm" fontWeight="normal" isRequired>
            Selecione sua modalidade de concorrência:
          </TitleSectionMobilePage>
          <FormControl display="flex" flexDir="column" isInvalid={!!formState.errors.modality_type?.message}>
            <Stack gap={2}>{modalityListFilter}</Stack>
            <FormErrorMessage fontSize="0.78rem">
              {formState.errors.modality_type?.message?.toString()}
            </FormErrorMessage>
          </FormControl>
        </Stack>
      )}
    </Stack>
  );
};

export default FormVacancyMobilePage;
